import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import Button from '../components/shared/Button'
import Spinner from '../components/shared/Spinner'
import Alert from '../components/shared/Alert'
import Card from '../components/shared/Card'
import logoSvg from '../assets/logo.svg'
import './Login.css'

export default function VerifyMagicLink() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { verifyMagicLink, user } = useAuth()

  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const token = searchParams.get('token')

    if (!token) {
      setStatus('error')
      setError('Invalid link. Please request a new sign-in link.')
      return
    }

    const verify = async () => {
      const result = await verifyMagicLink(token)

      if (result.success) {
        setStatus('success')
        // Redirect to dashboard after short delay
        setTimeout(() => {
          navigate('/dashboard', { replace: true })
        }, 1500)
      } else {
        setStatus('error')
        setError(result.error || 'This link has expired or is invalid.')
      }
    }

    verify()
  }, [searchParams, verifyMagicLink, navigate])

  // Already logged in
  if (user && status !== 'success') {
    return (
      <div className="login-page">
        <Link to="/" className="login-logo">
          <img src={logoSvg} alt="PlanScan" />
        </Link>

        <Card className="login-card">
          <div className="login-success">
            <div className="login-success-icon">✓</div>
            <h1 className="login-title">Already Signed In</h1>
            <p className="login-subtitle">
              You're already signed in as <strong>{user.email}</strong>
            </p>
            <Link to="/dashboard">
              <Button variant="primary" size="large">
                Go to Dashboard
              </Button>
            </Link>
          </div>
        </Card>
      </div>
    )
  }

  // Verifying state
  if (status === 'verifying') {
    return (
      <div className="login-page">
        <Link to="/" className="login-logo">
          <img src={logoSvg} alt="PlanScan" />
        </Link>

        <Card className="login-card">
          <div className="login-success">
            <Spinner size="large" showLabel label="Verifying your link..." />
            <h1 className="login-title">Signing You In</h1>
            <p className="login-subtitle">Please wait while we verify your link.</p>
          </div>
        </Card>
      </div>
    )
  }

  // Success state
  if (status === 'success') {
    return (
      <div className="login-page">
        <Link to="/" className="login-logo">
          <img src={logoSvg} alt="PlanScan" />
        </Link>

        <Card className="login-card">
          <div className="login-success">
            <div className="login-success-icon">✓</div>
            <h1 className="login-title">You're Signed In</h1>
            <p className="login-subtitle">
              Redirecting you to your dashboard...
            </p>
            <Spinner size="small" />
          </div>
        </Card>
      </div>
    )
  }

  // Error state
  return (
    <div className="login-page">
      <Link to="/" className="login-logo">
        <img src={logoSvg} alt="PlanScan" />
      </Link>

      <Card className="login-card">
        <h1 className="login-title">Link Expired</h1>

        <Alert variant="danger" className="login-alert">
          {error}
        </Alert>

        <p className="login-subtitle">
          Sign-in links expire after 15 minutes for security.
          Please request a new link.
        </p>

        <Link to="/login">
          <Button variant="primary" size="large" fullWidth>
            Request New Link
          </Button>
        </Link>
      </Card>
    </div>
  )
}
